import { useState } from "react";
import { makeStyles } from "@material-ui/styles";
import classNames from "classnames";
import BoxControls from "../../Common/Box/BoxControls";
import Table from "../../Common/Box/Table";
import ScenarioChooser from "../../Common/Box/Choosers/ScenarioChooser";
import initialData from "../../../data/initialData.json";
import useAreaQuery from "../../../hooks/useAreaQuery";
import useCsvDownload from "../../../hooks/useCsvDownload";
import {
  formatNumber,
  formatPercentage,
  getDownloadFileStem,
  getScenario,
} from "../../../utilities";

const key = "APAC_t9";
const number = 5;

const format = (n) =>
  formatNumber(n, {
    significantDigits: 3,
  });

const useStyles = makeStyles((theme) => ({
  changeGroupBoundary: {
    borderLeft: `1px solid ${theme.table.border.primary}`,
  },
  nameGroupBoundary: {
    borderRight: `1px solid ${theme.table.border.primary}`,
  },
  table: {
    borderCollapse: "collapse",
    fontSize: theme.table.fontSize,
    "& tbody tr": {
      borderTop: `1px solid ${theme.table.border.primary}`,
    },
    "& tbody tr:first-child": {
      borderTop: `1px solid ${theme.table.border.secondary}`,
    },
    "& tbody tr:last-child": {
      fontWeight: "bold",
      borderTop: `1px solid ${theme.table.border.secondary}`,
    },
    "& td": {
      padding: theme.table.cellPadding,
      textAlign: "right",
      whiteSpace: "nowrap",
    },
    "& td.category": {
      textAlign: "left",
    },
    "& th": {
      padding: theme.table.cellPadding,
      whiteSpace: "nowrap",
    },
    "& tr:last-child th:not(.category)": {
      minWidth: "8rem",
    },
  },
}));

const Component = () => {
  const [selectedScenarioId, setSelectedScenarioId] = useState(5);

  const downloadCsv = useCsvDownload();

  const { areaData: data } = useAreaQuery(
    `/apac/tables/9/${selectedScenarioId}`,
    initialData["apac_t9"]
  );

  const classes = useStyles();

  const getSubtitle = () => {
    switch (selectedScenarioId) {
      case 3:
        return "Scenario 2: Shift Safety Profile by Income Group, 2019-2030";

      case 4:
        return "Scenario 3: Shift Safety Profile & Method Mix by Income Group, 2019-2030";

      case 5:
        return "Scenario 1: Maintain Safety Profile & Method Mix by Income Group, 2019-2030";

      default:
        return "Future scenario by Income Group, 2019-2030";
    }
  };

  const handleScenarioChange = (scenarioId) => {
    setSelectedScenarioId(scenarioId);
  };

  const handleCsvDownload = () => {
    const fileStem = getDownloadFileStem(
      key,
      number,
      getScenario(selectedScenarioId)
    );

    downloadCsv(`/apac/tables/9/${selectedScenarioId}/csv`, `${fileStem}.csv`);
  };

  return (
    <Table
      key={key}
      number={number.toString()}
      title="Change in Abortions and PAC Services"
      subtitle={getSubtitle()}
      controls={
        <BoxControls>
          <ScenarioChooser
            area="abortion"
            value={selectedScenarioId}
            onChange={handleScenarioChange}
          />
        </BoxControls>
      }
      onDownloadCsv={handleCsvDownload}
    >
      <table className={classes.table}>
        <thead>
          <tr>
            <th className={classes.nameGroupBoundary} />
            <th colSpan={3} />
            <th colSpan={2} className={classes.changeGroupBoundary}>
              Change (2019 to 2030)
            </th>
          </tr>
          <tr>
            <th className={classes.nameGroupBoundary} />
            <th>2019</th>
            <th>2025</th>
            <th>2030</th>
            <th className={classes.changeGroupBoundary}>#</th>
            <th>%</th>
          </tr>
        </thead>
        <tbody>
          {data.map((incomeGroup) => (
            <tr key={incomeGroup.incomeGroup}>
              <td className={classNames("category", classes.nameGroupBoundary)}>
                {incomeGroup.incomeGroup}
              </td>

              {incomeGroup.years.map((year) => (
                <td key={year.year}>{format(year.value)}</td>
              ))}

              <td className={classes.changeGroupBoundary}>
                {format(incomeGroup.change)}
              </td>
              <td>{formatPercentage(incomeGroup.percentage)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Table>
  );
};

export default Component;
